import { format, parseISO } from 'date-fns';
import { t } from 'utils/getTranslations';
import { isNumeric } from 'utils/number';
import { CompositeFilter, CompositeFilterForm, CompositePart, CompositePartForm, SelectOption } from './ListTableTypes';

const isList = (part: CompositePart | CompositePartForm) =>
    part.type === 'multiselect' || part.type === 'checkboxgroup';

/** The parts as their arguments come: the fixed ones in their order, the list part last. */
export const inArgumentOrder = <P extends CompositePart | CompositePartForm>(parts: P[]) => [
    ...parts.filter((part) => !isList(part)),
    ...parts.filter(isList),
];

export const day = (value: string | null) => (value ? format(new Date(value), 'yyyy-MM-dd') : '');
const bound = (value: number | string | null) => (isNumeric(String(value)) ? String(value) : '');
export const unquoted = (value: SelectOption['value']) => String(value).replace(/^"|"$/g, '');
const range = (from: string, to: string) => (from || to ? [from, to].join(' – ').trim() : '');

const partArgs = (part: CompositePartForm): string[] => {
    switch (part.type) {
        case 'daterange':
            return [day(part.from), day(part.to)];
        case 'interval':
            return [bound(part.from), bound(part.to)];
        case 'number':
            return [part.value != null ? String(part.value) : ''];
        case 'dropdown':
            return [part.selected ? unquoted(part.selected.value) : ''];
        case 'multiselect':
            return part.selected.map((option) => unquoted(option.value));
        case 'checkboxgroup':
            return part.filters.filter((option) => option.isSelected).map((option) => option.argument);
    }
};

/** The clause's arguments, unquoted; none while no part but a dropdown holds a value. */
export const compositeArgs = (filter: CompositeFilterForm): string[] | undefined =>
    filter.parts.some((part) => part.type !== 'dropdown' && partArgs(part).some(Boolean))
        ? inArgumentOrder(filter.parts).flatMap(partArgs)
        : undefined;

/** The clause the filter sends, or none. */
export const compositeClause = (filter: CompositeFilterForm): string | undefined => {
    const args = compositeArgs(filter);
    if (!args) return undefined;
    const comparator = filter.isExcluding ? filter.comperator.replace(/^=(.+)=$/, '=not$1=') : filter.comperator;
    return `${filter.selector}${comparator}(${args.map((arg) => `"${arg}"`).join(',')})`;
};

/** The filter holding its clause's arguments, as `compositeArgs` gives them; with none, holding nothing. */
export const compositeFromArgs = (
    filter: CompositeFilter | CompositeFilterForm,
    args: string[] = [],
    isExcluding = false
): CompositeFilterForm => {
    let at = 0;
    const take = (count: number) => {
        const taken = args.slice(at, at + count).map(unquoted);
        at += count;
        return taken;
    };
    const formOf = (part: CompositePart | CompositePartForm): CompositePartForm => {
        switch (part.type) {
            case 'daterange': {
                const [from, to] = take(2);
                // `parseISO` reads a day as local midnight, as the date range filter reads its bounds.
                return {
                    ...part,
                    from: from ? parseISO(from).toISOString() : null,
                    to: to ? parseISO(to).toISOString() : null,
                };
            }
            case 'interval': {
                const [from, to] = take(2);
                return { ...part, from: isNumeric(from) ? Number(from) : null, to: isNumeric(to) ? Number(to) : null };
            }
            case 'number': {
                const [value] = take(1);
                return { ...part, value: isNumeric(value) ? Number(value) : null };
            }
            case 'dropdown': {
                const [value] = take(1);
                return {
                    ...part,
                    selected: part.options.find((option) => String(option.value) === value) ?? part.options[0] ?? null,
                };
            }
            case 'multiselect': {
                const values = take(args.length);
                return { ...part, selected: part.options.filter((option) => values.includes(String(option.value))) };
            }
            case 'checkboxgroup': {
                const values = take(args.length);
                return {
                    ...part,
                    filters: part.filters.map((option, index) => ({
                        ...option,
                        isSelected: values.includes(option.argument),
                        key: `${part.selector}${option.argument}${index}`,
                        value: option.argument,
                    })),
                };
            }
        }
    };
    const forms = new Map(inArgumentOrder(filter.parts).map((part) => [part, formOf(part)]));
    return {
        ...filter,
        parts: filter.parts.map((part) => forms.get(part) ?? formOf(part)),
        isExcluding: isExcluding && args.length > 0,
    };
};

const partText = (part: CompositePartForm, formatDate: (value: string) => string): string => {
    switch (part.type) {
        case 'daterange':
            return range(part.from ? formatDate(part.from) : '', part.to ? formatDate(part.to) : '');
        case 'interval':
            return range(bound(part.from), bound(part.to));
        case 'number':
            return part.value != null ? String(part.value) : '';
        case 'dropdown':
            // Mid-sentence in the chip: "20000 – · last 12 months".
            return part.selected ? String(part.selected.label).toLocaleLowerCase() : '';
        case 'multiselect':
            return part.selected.map((option) => option.label).join(', ');
        case 'checkboxgroup':
            return part.filters
                .filter((option) => option.isSelected)
                .map((option) => option.label)
                .join(', ');
    }
};

/** What the chip reads, part by part in argument order: the list part's values last. */
export const compositeSummary = (filter: CompositeFilterForm, formatDate: (value: string) => string): string[] =>
    inArgumentOrder(filter.parts).flatMap((part) => {
        const text = partText(part, formatDate);
        if (!text) return [];
        return [part.chipText ? t(part.chipText, { value: text }) : text];
    });
